import React, { useState, useEffect, useRef } from 'react';
import { Container, Box, Typography, CircularProgress, Paper } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { StepNavigation } from './StepNavigation';
import {
  matchPublications,
  Publication,
  PublicationMatchRequest,
  aiRankPublications
} from '../../services/distributionApi';
import { getPressRelease } from '../../services/prApi';
import {
  publications as allPublications,
  Publication as PublicationData
} from '../../data/distributionData';
import { SelectionData } from './types';

// Arc Animation Component
const CircularPublicationItem: React.FC<{
  publication: string;
  angle: number;
  radius: number;
  isActive: boolean;
  opacity: number;
}> = ({ publication, angle, radius, isActive, opacity }) => {
  const x = radius * Math.cos((angle * Math.PI) / 180);
  const y = radius * Math.sin((angle * Math.PI) / 180);

  return (
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      opacity
    }}>
      <Paper elevation={isActive ? 6 : 2} style={{
        padding: '10px 18px',
        backgroundColor: isActive ? '#ff5200' : '#ffffff',
        color: isActive ? '#ffffff' : '#09273b',
        borderRadius: '20px',
        fontWeight: isActive ? 700 : 500,
        fontSize: isActive ? '15px' : '13px',
        transform: isActive ? 'scale(1.15)' : 'scale(1)',
        transition: 'all 0.4s ease',
        boxShadow: isActive ? '0 6px 24px rgba(255, 82, 0, 0.4)' : '0 2px 8px rgba(9, 39, 59, 0.1)',
        whiteSpace: 'nowrap',
        border: isActive ? '2px solid #fff' : '1px solid #e0e0e0'
      }}>
        {publication}
      </Paper>
    </div>
  );
};

interface RealTimePublicationScanningProps {
  searchCriteria: SelectionData | undefined;
  prId?: string | null;
  onComplete: (publications: Publication[]) => void;
  onBack?: () => void;
}

export const RealTimePublicationScanning: React.FC<RealTimePublicationScanningProps> = ({
  searchCriteria,
  prId,
  onComplete,
  onBack
}) => {
  const [scanning, setScanning] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [foundPublications, setFoundPublications] = useState<Publication[]>([]);
  const [visiblePublications, setVisiblePublications] = useState<string[]>([]);
  const publicationsRef = useRef<Publication[]>([]);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isAnimationCompleteRef = useRef(false);

  // Helper function to map geography selection to countries/regions
  const getGeographyCountries = (geography: string[]): string[] => {
    if (!geography || geography.length === 0 || geography.includes('global')) {
      return Array.from(new Set(allPublications.map(p => p.country)));
    }

    // Build geography mapping dynamically from publications
    const geographyMap: { [key: string]: string[] } = {};

    // Group publications by region
    allPublications.forEach(pub => {
      const region = pub.region.toLowerCase().replace(/\s+/g, '-');
      if (!geographyMap[region]) {
        geographyMap[region] = [];
      }
      if (!geographyMap[region].includes(pub.country)) {
        geographyMap[region].push(pub.country);
      }
    });

    const selectedCountries: string[] = [];
    geography.forEach(geo => {
      const normalizedGeo = geo.toLowerCase().replace(/\s+/g, '-');
      if (geographyMap[normalizedGeo]) {
        selectedCountries.push(...geographyMap[normalizedGeo]);
      }
    });

    return selectedCountries.length > 0 ? selectedCountries : Array.from(new Set(allPublications.map(p => p.country)));
  };

  // Get filtered publications from unified array
  const getTierFallbackPublications = (selectedTiers: string[], selectedGeography: string[] = [], selectedIndustries: string[] = []): PublicationData[] => {
    // If no tiers selected, default to all tiers. "corp-crunch" maps to tier 1 for fetching fallbacks.
    const tiersToUse = selectedTiers.length > 0
      ? selectedTiers.map(t => (t === 'corp-crunch' ? 1 : parseInt(t, 10)))
      : [1, 2, 3, 4, 5];

    // Filter publications based on criteria
    let filtered = allPublications.filter(pub => {
      // Tier matching
      const tierMatch = tiersToUse.includes(pub.tier);

      // Geography matching
      let geoMatch = true;
      if (selectedGeography.length > 0 && !selectedGeography.includes('global')) {
        const normalizedGeos = selectedGeography.map(g => g.toLowerCase().replace(/\s+/g, '-'));
        const pubRegion = pub.region.toLowerCase().replace(/\s+/g, '-');
        const pubCountry = pub.country.toLowerCase();

        geoMatch = normalizedGeos.some(geo =>
          pubRegion.includes(geo) ||
          geo.includes(pubRegion) ||
          pubCountry.includes(geo) ||
          geo.includes(pubCountry)
        ) || pub.region.toLowerCase() === 'global';
      }

      // Industry matching
      let industryMatch = true;
      if (selectedIndustries.length > 0) {
        const normalizedIndustries = selectedIndustries.map(ind => ind.toLowerCase().replace(/-/g, ' '));
        const primaryIndustry = pub.primaryIndustry.toLowerCase();
        const secondaryIndustry = pub.secondaryIndustry.toLowerCase();

        industryMatch = normalizedIndustries.some(ind =>
          primaryIndustry.includes(ind) ||
          ind.includes(primaryIndustry) ||
          secondaryIndustry.includes(ind) ||
          ind.includes(secondaryIndustry)
        );
      }

      return tierMatch && geoMatch && industryMatch;
    });

    // Shuffle array to show different results each time
    const shuffled = [...filtered];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  };

  // Helper to calculate DA based on tier
  const calculateDA = (tier: number): number => {
    const daMap: { [key: number]: number } = {
      1: 93,
      2: 85,
      3: 78,
      4: 70,
      5: 65
    };
    return daMap[tier] || 70;
  };

  // Helper to calculate monthly reach based on tier
  const calculateMonthlyReach = (tier: number): number => {
    const reachMap: { [key: number]: number } = {
      1: 100,
      2: 75,
      3: 50,
      4: 30,
      5: 20
    };
    return reachMap[tier] || 20;
  };

  useEffect(() => {
    const runMatching = async () => {
      try {
        // Extract selection criteria first
        const selectedTierNumbers = (searchCriteria?.tier || [])
          .map(t => (t === 'corp-crunch' ? 1 : parseInt(t, 10)))
          .filter(n => !isNaN(n));

        const selectedGeographies = searchCriteria?.geography || [];
        const selectedIndustries = searchCriteria?.industry || [];

        const criteria: PublicationMatchRequest = {
          geography: selectedGeographies,
          industry: selectedIndustries,
          subClusters: searchCriteria?.subClusters || [],
          tiers: searchCriteria?.tier || [],
          prId: prId || undefined,
        };

        // IMMEDIATELY create fallback publications and start animation
        console.log('🚀 Starting ARC animation immediately with fallback publications...');

        const filteredPublications = getTierFallbackPublications(
          searchCriteria?.tier || [],
          selectedGeographies,
          selectedIndustries
        );

        // Map publications to Publication objects with proper fields from unified array
        const mappedPubs: Publication[] = filteredPublications.map((pub, index) => {
          // Add variety to metrics based on index to show different results
          const variation = index % 10;

          return {
            id: `${pub.name.toLowerCase().replace(/\s+/g, '-')}-${index}-${Date.now()}`,
            name: pub.name,
            tier: pub.tier,
            domainAuthority: calculateDA(pub.tier) + variation,
            monthlyReach: calculateMonthlyReach(pub.tier) + (variation * 5),
            engagementRate: (pub.tier === 1 ? 25 : pub.tier === 2 ? 20 : pub.tier === 3 ? 15 : 10) + variation,
            topicRelevance: 80 + (variation % 15),
            country: pub.country,
            geographicRegions: pub.region === 'Global' ? ['global'] : [pub.region.toLowerCase().replace(/\s+/g, '-')],
            email: `editor@${pub.website}`,
          };
        });

        let pubs: Publication[] = mappedPubs;
        console.log(`✅ Created ${pubs.length} fallback publications - starting ARC animation immediately`);

        // Set initial publications and start animation IMMEDIATELY
        publicationsRef.current = pubs;
        setFoundPublications(pubs);

        // Start ARC animation immediately with filtered publications (by region and tier)
        let index = 0;
        let names = pubs.map(p => p.name);
        isAnimationCompleteRef.current = false;

        // Adjust animation speed - make it slower and smoother (250ms instead of 50ms)
        const maxPubs = 50;
        if (names.length > maxPubs) {
          names = names.slice(0, maxPubs);
          pubs = pubs.slice(0, maxPubs);
          publicationsRef.current = pubs;
        }
        const animationSpeed = 250; // Slower - 250ms for better visibility
        const windowSize = 8;

        // Start animation immediately with first batch
        if (names.length > 0) {
          setVisiblePublications(
            names.slice(0, Math.min(windowSize, names.length))
          );
        }

        // Function to update animation with new publications (from API or AI)
        const updateAnimationWithNewPubs = (newPubs: Publication[]) => {
          const newNames = newPubs.map(p => p.name);
          names = newNames;
          publicationsRef.current = newPubs;
          setFoundPublications(newPubs);

          // If animation is still running, update it smoothly
          if (!isAnimationCompleteRef.current && index < names.length) {
            const halfWindow = Math.floor(windowSize / 2);
            const start = Math.max(0, index - halfWindow);
            const end = Math.min(names.length, index + halfWindow + 1);
            setVisiblePublications(names.slice(start, end));
          } else if (isAnimationCompleteRef.current) {
            // If animation completed, restart with new publications
            index = 0;
            setCurrentIndex(0);
            isAnimationCompleteRef.current = false;
            setScanning(true);
            setVisiblePublications(
              newPubs.slice(0, Math.min(windowSize, newPubs.length)).map(p => p.name)
            );

            // Restart animation
            if (animationIntervalRef.current) {
              clearInterval(animationIntervalRef.current);
            }
            startAnimation();
          }
        };

        // Start animation function
        const startAnimation = () => {
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
          }

          animationIntervalRef.current = setInterval(() => {
            if (index < names.length) {
              setCurrentIndex(index);
              const halfWindow = Math.floor(windowSize / 2);
              const start = Math.max(0, index - halfWindow);
              const end = Math.min(names.length, index + halfWindow + 1);
              setVisiblePublications(names.slice(start, end));
              index++;
            } else {
              if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
              }
              isAnimationCompleteRef.current = true;
              // Complete quickly if no AI is running
              if (!prId) {
                setScanning(false);
                setTimeout(() => onComplete(publicationsRef.current), 300);
              }
            }
          }, animationSpeed);
        };

        // Start animation immediately
        startAnimation();

        // Now fetch from API in background (non-blocking) - don't await
        console.log('🔍 Fetching publications from API in background (ARC already showing)...');
        (async () => {
          try {
            const response = await matchPublications(criteria);
            const apiPubs = Array.isArray(response) ? response : [];
            console.log(`✅ API returned ${apiPubs.length} publications from backend database`);

            if (apiPubs.length > 0) {
              // Filter API results by tier and geography
              let filteredApiPubs = apiPubs;

              if (selectedTierNumbers.length > 0) {
                // Filter by tier - ensure we're comparing numbers correctly
                filteredApiPubs = filteredApiPubs.filter(pub => {
                  const pubTier = typeof pub.tier === 'string' ? parseInt(pub.tier, 10) : pub.tier;
                  return selectedTierNumbers.includes(pubTier);
                });
              }

              if (selectedGeographies.length > 0 && !selectedGeographies.includes('global')) {
                filteredApiPubs = filteredApiPubs.filter(pub => {
                  const pubRegions = (pub as any).geographicRegions || [];
                  const pubCountry = pub.country?.toLowerCase() || '';

                  return selectedGeographies.some(selectedGeo => {
                    const normalizedSelected = selectedGeo.toLowerCase().trim();
                    if (pubRegions.some((r: string) => {
                      const normalizedRegion = String(r).toLowerCase().trim();
                      return normalizedRegion === normalizedSelected ||
                        normalizedRegion.includes(normalizedSelected) ||
                        normalizedSelected.includes(normalizedRegion);
                    })) {
                      return true;
                    }
                    const normalizedCountry = pubCountry.toLowerCase().trim();
                    return normalizedCountry === normalizedSelected ||
                      normalizedCountry.includes(normalizedSelected) ||
                      normalizedSelected.includes(normalizedCountry);
                  });
                });
              }

              if (filteredApiPubs.length > 0) {
                console.log(`✅ Using ${filteredApiPubs.length} publications from API (replacing fallback)`);
                pubs = filteredApiPubs;
                updateAnimationWithNewPubs(pubs);
              }
            }
          } catch (e: any) {
            console.warn('⚠️ API call failed, continuing with fallback publications:', e.message);
            // Continue with fallback publications
          }

          // After API results (or if API failed), run AI ranking in background
          if (pubs.length > 0 && prId) {
            // Don't await - let it run in background while animation plays
            (async () => {
              try {
                console.log('🤖 Starting AI ranking in background (ARC animation already showing publications)...');
                // Fetch PR content for AI analysis
                let prContent: string | undefined;
                try {
                  const prData = await getPressRelease(prId);
                  prContent = prData.content;
                  console.log(`✅ Fetched PR content (${prContent?.length || 0} characters) for AI analysis`);
                } catch (e) {
                  console.warn('⚠️ Could not fetch PR content for AI ranking:', e);
                }

                // Call AI ranking API (this takes time)
                console.log(`🤖 AI is analyzing ${pubs.length} publications to find best matches...`);
                const aiRankedPubs = await aiRankPublications(pubs, prId, prContent);

                if (aiRankedPubs && aiRankedPubs.length > 0) {
                  console.log(`✅ AI ranked ${aiRankedPubs.length} publications - updating ARC with best matches`);
                  // Update publications with AI-ranked results
                  updateAnimationWithNewPubs(aiRankedPubs);
                } else {
                  console.log('⚠️ AI ranking returned empty, keeping original order');
                  // Complete animation if it was waiting
                  if (isAnimationCompleteRef.current) {
                    setScanning(false);
                    setTimeout(() => onComplete(publicationsRef.current), 800);
                  }
                }
              } catch (aiError: any) {
                console.error('❌ Error during AI ranking (non-critical):', aiError);
                // Complete animation if it was waiting
                if (isAnimationCompleteRef.current) {
                  setScanning(false);
                  setTimeout(() => onComplete(publicationsRef.current), 800);
                }
              }
            })();
          } else {
            // No AI ranking - complete animation normally after it finishes
            if (pubs.length > 0 && !prId) {
              console.log('⚠️ No prId provided, skipping AI ranking - animation will complete normally');
            }

            // Set timeout to complete animation if no AI is running
            const estimatedAnimationTime = Math.min(names.length * animationSpeed, 5000); // Max 5 seconds
            setTimeout(() => {
              if (isAnimationCompleteRef.current && !prId) {
                setScanning(false);
                setTimeout(() => onComplete(publicationsRef.current), 500);
              }
            }, estimatedAnimationTime + 500);
          }
        })(); // End of async API call block

        // Force completion after max 8 seconds total (including AI if running)
        const maxCompletionTime = 8000;
        setTimeout(() => {
          if (scanning) {
            setScanning(false);
            setTimeout(() => onComplete(publicationsRef.current), 300);
          }
        }, maxCompletionTime);

        return () => {
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }
        };
      } catch (error) {
        console.error('Error matching publications:', error);
        setScanning(false);
        onComplete([]);
      }
    };

    runMatching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAngle = (idx: number, total: number) => total === 1 ? 270 : 180 + (idx / (total - 1)) * 180;

  return (
    <Container maxWidth="lg">
      <StepNavigation onBack={onBack} />
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 6, fontSize: { xs: '2rem', md: '3rem' } }}>
          {scanning ? 'Matching Publications...' : (() => {
            const tierText = searchCriteria?.tier?.length
              ? `Tier ${searchCriteria.tier.join(', ')}`
              : 'all tiers';
            const geoText = searchCriteria?.geography?.length
              ? searchCriteria.geography.map(g => {
                // Convert geography IDs to readable names
                const geoMap: { [key: string]: string } = {
                  'global': 'Global',
                  'north-america': 'North America',
                  'south-america': 'South America',
                  'europe': 'Europe',
                  'asia': 'Asia Pacific',
                  'middle-east': 'Middle East',
                  'africa': 'Africa'
                };
                return geoMap[g.toLowerCase()] || g;
              }).join(', ')
              : 'all regions';
            return `We have matched you with ${foundPublications.length} publication${foundPublications.length !== 1 ? 's' : ''} based on your selected ${tierText} and ${geoText}`;
          })()}
        </Typography>

        <Box sx={{ position: 'relative', width: '100%', height: { xs: '350px', md: '450px' }, mb: 6 }}>
          <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
            <Box sx={{
              width: { xs: 120, md: 140 }, height: { xs: 120, md: 140 }, borderRadius: '50%',
              backgroundColor: scanning ? '#ff5200' : '#4caf50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: scanning ? '0 8px 32px rgba(255, 82, 0, 0.3)' : '0 8px 32px rgba(76, 175, 80, 0.3)',
              transition: 'all 0.6s ease'
            }}>
              {scanning ? <CircularProgress size={80} thickness={2} sx={{ color: '#fff' }} /> : <CheckCircleIcon sx={{ fontSize: 80, color: '#fff' }} />}
            </Box>
          </Box>
          {visiblePublications.map((pub, idx) => (
            <CircularPublicationItem key={`${pub}-${idx}`} publication={pub}
              angle={getAngle(idx, visiblePublications.length)} radius={window.innerWidth < 768 ? 180 : 220}
              isActive={idx === Math.floor(visiblePublications.length / 2)}
              opacity={Math.max(0.4, 1 - Math.abs(idx - Math.floor(visiblePublications.length / 2)) * 0.15)} />
          ))}
        </Box>

        {!scanning && (
          <Typography variant="h5" sx={{ color: '#09273b', fontWeight: 600 }}>
            Check out to confirm your release
          </Typography>
        )}
      </Box>
    </Container>
  );
};